import { money } from '~/util/string'

// The max rate, like the premium Oracle quotes (monthly_rate_max * 12); a
// missing or non-positive rate is no rate.
const rate = (rates, key) => {
    const value = Number(rates[`${key}_max`] ?? rates[`${key}_min`])
    return value > 0 ? value : null
}

const cents = value => (Number.isFinite(value) ? Math.round(value * 100) / 100 : null)

// A Medigap plan's household discount, from medicare.gov's household rates
// inline in the quote's `rates`. Oracle fills `household_discount` ('7%') by
// the same rule (oracle/quotes/supp.py household_discount_from_rates) and its
// value wins; quotes cached before Oracle did are worked out here.
//
// A client who lives with a roommate (`clientChoice` = the survey's
// household_discount) gets the roommate rate only; anyone else the standard
// rate, else the roommate one. A household rate at or above the regular rate
// (HealthSpring lists 1.74x, maybe a price for two) is a discount without a
// percent we can stand behind.
//
// Returns { percent, monthlyPremium } with the discount applied to the premium
// shown (a plan override can set it apart from the rates), { percent: null,
// monthlyPremium: null } for a discount without a percent, or null.
export function householdDiscount (plan, clientChoice = null) {
    if (!plan || plan.plan_type !== 'supp') {
        return null
    }
    const rates = plan.rates || {}
    const base = rate(rates, 'monthly_rate')
    const household = clientChoice === 'ROOMMATE'
        ? rate(rates, 'monthly_rate_hhd_roommate')
        : rate(rates, 'monthly_rate_hhd_standard') ?? rate(rates, 'monthly_rate_hhd_roommate')
    const premium = parseFloat(plan.monthly_premium)
    const priced = base && household && household < base

    const oracle = parseFloat(plan.household_discount)
    if (oracle > 0) {
        return { percent: oracle, monthlyPremium: cents(premium * (priced ? household / base : 1 - oracle / 100)) }
    }
    if (!base || !household) {
        return null
    }
    if (!priced) {
        return { percent: null, monthlyPremium: null }
    }
    const percent = Math.round((base - household) / base * 100)
    return percent ? { percent, monthlyPremium: cents(premium * household / base) } : null
}

// The plan card's line for the fields parsePlan sets from householdDiscount.
export function householdDiscountLabel (plan) {
    if (plan?.household_discount) {
        const price = plan.household_discount_monthly_premium
        return `${plan.household_discount} household discount${price ? `: ${money(price)}/month` : ''}`
    }
    return plan?.household_discount_available ? 'Household discount available' : null
}
